import { useRef } from "react";
import type { Table } from "@tanstack/react-table";
import type { Profile } from "@/services/api/companies";
import { useScrollPositionPreservation } from "./hooks/useScrollPositionPreservation";
import { useDragScroll } from "./hooks/useDragScroll";
import { useScrollButtons } from "./hooks/useScrollButtons";
import { ScrollButtons } from "./components/ScrollButtons";
import { TableContent } from "./components/TableContent";
import "./companyStaffScroll.css";

interface CompanyStaffTableProps {
  table: Table<Profile>;
  isLoading: boolean;
  profiles: Profile[];
  parentScrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

export function CompanyStaffTable({
  table,
  isLoading,
  profiles,
  parentScrollContainerRef,
}: CompanyStaffTableProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
    checkScrollability,
  } = useScrollButtons(scrollContainerRef);

  useScrollPositionPreservation(
    scrollContainerRef,
    parentScrollContainerRef,
    table,
    isLoading,
    profiles.length,
    checkScrollability
  );

  const dragHandlers = useDragScroll(scrollContainerRef);

  return (
    <div className="relative w-full h-full max-w-full overflow-x-auto overflow-y-auto min-w-0">
      <ScrollButtons
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        isLoading={isLoading}
        onScrollLeft={scrollLeft}
        onScrollRight={scrollRight}
      />

      <div
        ref={scrollContainerRef}
        className="company-staff-table-scroll w-full h-full max-w-full min-w-0"
        onMouseDown={dragHandlers.handleMouseDown}
        onMouseMove={dragHandlers.handleMouseMove}
        onMouseLeave={dragHandlers.handleMouseLeave}
        onMouseUp={dragHandlers.handleMouseUp}
        onScroll={checkScrollability}
      >
        <TableContent table={table} isLoading={isLoading} profiles={profiles} />
      </div>
    </div>
  );
}
